/**
 * useSavedReport — loads a saved report and applies its config to URL state.
 *
 * Encapsulates the pattern duplicated across all 4 Builder components:
 *   1. Fetch saved report by ID using react-query
 *   2. Apply config to URL state (once, respecting existing URL params)
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';

interface SavedReportData {
  report: {
    name: string;
    description?: string;
    config: Record<string, unknown>;
  };
}

interface UseSavedReportOptions<T> {
  /** The report ID from the URL (useParams). undefined = no saved report. */
  reportId: string | undefined;
  /** The section ID (e.g., 'product', 'marketing'). */
  sectionId: string;
  /** Current URL search params. */
  searchParams: URLSearchParams;
  /** The state update function from the section's state hook. */
  update: (patch: Partial<T>) => void;
  /**
   * Maps the saved config object into a state patch.
   * Called once when the report loads. Return the desired patch —
   * keys already present in the URL will be automatically excluded.
   */
  mapConfig: (config: Record<string, unknown>) => Partial<T>;
  /** Keys to skip when checking URL overrides (e.g., 'type' for marketing). */
  skipKeys?: string[];
}

export function useSavedReport<T>({
  reportId,
  sectionId,
  searchParams,
  update,
  mapConfig,
  skipKeys = [],
}: UseSavedReportOptions<T>) {
  const { data: savedReport } = useQuery<SavedReportData>({
    queryKey: ['report', reportId],
    queryFn: () => api.get<SavedReportData>(endpoints.reports.get(reportId!, sectionId)),
    enabled: !!reportId,
  });

  const configAppliedRef = useRef(false);

  useEffect(() => {
    const config = savedReport?.report?.config;
    if (!config || configAppliedRef.current) return;
    configAppliedRef.current = true;

    const patch = mapConfig(config);

    // Only apply fields that aren't already in the URL (URL overrides win)
    const filtered: Partial<T> = {};
    for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
      if (skipKeys.includes(key)) continue;
      if (!searchParams.has(key) && value !== undefined) {
        (filtered as Record<string, unknown>)[key] = value;
      }
    }

    if (Object.keys(filtered).length > 0) {
      update(filtered);
    }
  }, [savedReport, searchParams, update, mapConfig, skipKeys]);

  return { savedReport };
}
